import { SingleDeleteRecord } from '@/features/invoice_reports/components/ConfirmDeleteModal/context/ConfirmDeleteModalContext';
import { ActionCellRecord } from '@/features/invoice_reports/components/List/Table/Cells/components/ActionCell/ActionCell';
import type { DuplicateParams } from '@/features/invoice_reports/components/ConfirmDuplicateModal/context/ConfirmDuplicateModalContext';
import type { CancelRecord } from '@/features/invoice_reports/components/ConfirmCancelModal/context/ConfirmCancelModalContext';
import type { ActionMenuItems } from '@moneyforward/ap-frontend-components';
import { useCallback, useMemo, useState } from 'react';

export type UseApplicationListActionMenuOptions = {
  record: ActionCellRecord;
  t: (key: string, options?: Record<string, string>) => string;
  onDuplicate?: (params: DuplicateParams) => Promise<void>;
  onDelete?: (record: SingleDeleteRecord) => void;
  onCancel?: (record: CancelRecord) => void;
};

/**
 * Application list action menu (NA-4165).
 * Driven by record edit_action, delete_action, cancel_action: visible = show, allowed = enabled, reason = tooltip.
 */
export function useApplicationListActionMenu({
  record,
  t,
  onDuplicate,
  onDelete,
  onCancel,
}: UseApplicationListActionMenuOptions): {
  isEditVisible: boolean;
  isEditDisabled: boolean;
  editHref: string;
  menuItems: ActionMenuItems;
  editDisabledTooltip: string | undefined;
  deleteMenuItemTooltip: string | undefined;
  cancelMenuItemTooltip: string | undefined;
} {
  const [isCopying, setIsCopying] = useState(false);

  const handleDuplicateClick = useCallback(async () => {
    if (!onDuplicate) return;
    setIsCopying(true);
    try {
      await onDuplicate({
        id: record.id,
        number: record.number,
        title: record.title,
        apPayeeName: record.ap_payee_name,
        totalAmount: record.total_amount === '-' ? '' : record.total_amount,
      });
    } finally {
      setIsCopying(false);
    }
  }, [record, onDuplicate]);

  return useMemo(() => {
    const { edit_action, delete_action, cancel_action } = record;

    const isEditVisible = edit_action.visible;
    const isEditDisabled = !edit_action.allowed;
    const editHref = record.edit_path;
    const editDisabledTooltip = edit_action.reason ?? undefined;
    const deleteMenuItemTooltip = delete_action.allowed
      ? undefined
      : delete_action.reason ?? undefined;
    const cancelMenuItemTooltip = cancel_action.allowed
      ? undefined
      : cancel_action.reason ?? undefined;

    const menuItems: ActionMenuItems = [
      {
        key: 'detail',
        label: t('detail'),
        onClick: () => {
          window.location.href = record.detail_path;
        },
      },
      {
        key: 'duplicate',
        label: isCopying ? t('commons.processing') : t('duplicate'),
        disabled: !record.is_copyable || isCopying || !onDuplicate,
        onClick: handleDuplicateClick,
      },
      ...(delete_action.visible
        ? [
            {
              key: 'delete',
              label: t('delete'),
              disabled: !delete_action.allowed,
              onClick: () =>
                onDelete?.({
                  id: record.id,
                  number: record.number,
                  title: record.title,
                  apPayeeName: record.ap_payee_name,
                  totalAmount:
                    record.total_amount === '-' ? '' : record.total_amount,
                }),
            },
          ]
        : []),
      ...(cancel_action.visible
        ? [
            {
              key: 'cancel',
              label: t('ap_report_status_canceled'),
              disabled: !cancel_action.allowed,
              onClick: () =>
                onCancel?.({
                  id: record.id,
                  number: record.number,
                  title: record.title,
                  apPayeeName: record.ap_payee_name,
                  totalAmount:
                    record.total_amount === '-' ? '' : record.total_amount,
                }),
            },
          ]
        : []),
    ];

    return {
      isEditVisible,
      isEditDisabled,
      editHref,
      menuItems,
      editDisabledTooltip,
      deleteMenuItemTooltip,
      cancelMenuItemTooltip,
    };
  }, [
    record,
    t,
    onDuplicate,
    onDelete,
    onCancel,
    isCopying,
    handleDuplicateClick,
  ]);
}
